// Browser translation tools (Chrome's Google Translate in particular) rewrite the live DOM by
// wrapping text nodes in <font> elements. React's virtual DOM still expects those text nodes to
// be direct children of their parent, so the next in-place text update throws
// "NotFoundError: Failed to execute 'removeChild' on 'Node'" and crashes to the error boundary,
// even though the underlying mutation succeeded. Known upstream issue:
// https://github.com/facebook/react/issues/11538#issuecomment-417504600
//
// This installs the workaround recommended in that issue: removeChild/insertBefore become no-ops
// when the target node has been re-parented by the translator, instead of throwing.

let registered = false;

// Translation rewrites hit the guard on nearly every affected update, so warn once per method
// per session to keep the console usable for the very users the guard protects.
const warned = { removeChild: false, insertBefore: false };

const warnOnce = (method: keyof typeof warned, node: Node, parent: Node): void => {
  if (warned[method]) return;
  warned[method] = true;
  console.warn(`Skipped ${method} on a node re-parented by a browser translation tool (further occurrences suppressed)`, node, parent);
};

export const registerDomTranslationGuard = (): void => {
  if (registered || typeof Node !== 'function' || !Node.prototype) return;
  registered = true;

  const originalRemoveChild = Node.prototype.removeChild;
  Node.prototype.removeChild = function removeChild<T extends Node>(this: Node, child: T): T {
    if (child.parentNode !== this) {
      warnOnce('removeChild', child, this);
      return child;
    }
    return originalRemoveChild.call(this, child) as T;
  };

  const originalInsertBefore = Node.prototype.insertBefore;
  Node.prototype.insertBefore = function insertBefore<T extends Node>(
    this: Node,
    newNode: T,
    referenceNode: Node | null,
  ): T {
    if (referenceNode && referenceNode.parentNode !== this) {
      warnOnce('insertBefore', referenceNode, this);
      return newNode;
    }
    return originalInsertBefore.call(this, newNode, referenceNode) as T;
  };
};
